// src/components/SessionGate.jsx
//
// ✅ Revisa si en este dispositivo ya hay una sesión guardada cuando la
// persona abre la página de inicio (petfam.club / qa.petfam.club).
//
// - Sesión válida (200)          -> entra directo a /interfaz (o /welcomepet
//                                   si no tiene mascotas), igual que el login.
// - Cuenta con restricción (423) -> muestra el inicio normal. El token NO se
//                                   borra: cuando toque "Iniciar sesión", el
//                                   login le mostrará el modal de pago.
// - Token vencido/inválido (401) -> se borra la sesión y muestra el inicio.
// - Sin internet / servidor lento -> muestra el inicio sin borrar nada.
//
// Usa el mismo endpoint (POST /auth/session) y guarda los mismos datos en
// localStorage que Login.jsx, para que Interfaz funcione exactamente igual.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

// Misma URL base que usa useBackend (el hook del login)
const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

// Si el backend no responde en este tiempo, se muestra el inicio normal
// para que nadie se quede mirando una pantalla vacía.
const SESSION_CHECK_TIMEOUT_MS = 5000;

function clearSavedSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("idUser");
  localStorage.removeItem("petfamUser");
  localStorage.removeItem("userRole");
  localStorage.removeItem("userRoles");
  localStorage.removeItem("userEmail");
}

async function userHasPets(idUser) {
  try {
    const res = await authFetch(`/pets/user/${idUser}`);
    const json = await res.json().catch(() => null);
    const list = json?.data;
    return Array.isArray(list) && list.length > 0;
  } catch {
    // Igual que en Login.jsx: ante la duda, se manda a la interfaz
    return true;
  }
}

export default function SessionGate({ children }) {
  const navigate = useNavigate();

  // Solo se muestra la pantalla de carga si hay un token guardado
  const [checking, setChecking] = useState(() => !!localStorage.getItem("token"));

  useEffect(() => {
    const savedToken = localStorage.getItem("token");

    if (!savedToken) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    let redirected = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS);

    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${savedToken}`,
          },
          body: "{}",
          signal: controller.signal,
        });

        const json = await res.json().catch(() => null);
        if (cancelled) return;

        const status = json?.status;
        const data = json?.data;

        if (res.ok && status === 200 && data) {
          const newId = data.idUser || data.id || data.userId;
          const newToken = json.token || data.token || data.jwt || data.accessToken || null;

          if (newId && newToken) {
            // Mismos datos que guarda Login.jsx al iniciar sesión
            localStorage.setItem("token", newToken);

            const rawRoles = Array.isArray(data.roles) ? data.roles : [];
            localStorage.setItem("userRoles", JSON.stringify(rawRoles));

            let mainRole = null;
            if (rawRoles.includes("ROLE_ADMIN")) mainRole = "ADMIN";
            else if (rawRoles.includes("ROLE_CLIENTE")) mainRole = "CLIENTE";
            if (mainRole) localStorage.setItem("userRole", mainRole);

            localStorage.setItem("petfamUser", JSON.stringify(data));
            localStorage.setItem("idUser", String(newId));
            if (data.email) localStorage.setItem("userEmail", data.email);

            let target = "/interfaz";
            if (mainRole === "ADMIN") {
              target = "/admin";
            } else {
              const hasPets = await userHasPets(Number(newId));
              if (cancelled) return;
              if (!hasPets) target = "/welcomepet";
            }

            redirected = true;
            navigate(target, { replace: true, state: { from: "login", transition: "up" } });
            return;
          }
        }

        // Token vencido o inválido: se borra para no volver a intentarlo
        if (status === 401 || status === 403) {
          clearSavedSession();
        }

        // 423 (cuenta con restricción) u otro estado: se muestra el inicio
        // sin borrar el token.
      } catch {
        // Timeout o error de red: se muestra el inicio sin borrar nada.
      } finally {
        clearTimeout(timer);
        if (!cancelled && !redirected) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [navigate]);

  if (checking) {
    // Sin texto ni spinner: solo el fondo crema de la marca por una fracción
    // de segundo, y luego pasa directo al perfil (o muestra el inicio).
    return <div aria-busy="true" style={{ minHeight: "100vh", background: "#fff4e1" }} />;
  }

  return children;
}